import { useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from "react-icons/fa";
import classes from "./ToastHost.module.css";

const TOAST_EVENT = "mit-toast";
const TOAST_DURATION = 3000;

function toastIcon(type) {
  switch (type) {
    case "success":
      return <FaCheckCircle aria-hidden="true" />;
    case "error":
      return <FaTimes aria-hidden="true" />;
    case "warn":
      return <FaExclamationTriangle aria-hidden="true" />;
    default:
      return <FaInfoCircle aria-hidden="true" />;
  }
}

function toastClassName(type) {
  switch (type) {
    case "success":
      return classes.success;
    case "error":
      return classes.error;
    case "warn":
      return classes.warn;
    default:
      return classes.info;
  }
}

export default function ToastHost() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handleToast = (event) => {
      const detail = event?.detail || {};
      const message = String(detail.message || "").trim();

      if (!message) {
        return;
      }

      const id =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      setToasts((current) => [
        ...current.slice(-2),
        {
          id,
          type: detail.type || "info",
          message,
        },
      ]);
    };

    window.addEventListener(TOAST_EVENT, handleToast);
    return () => window.removeEventListener(TOAST_EVENT, handleToast);
  }, []);

  useEffect(() => {
    if (!toasts.length) {
      return undefined;
    }

    const timers = toasts.map((toast) =>
      window.setTimeout(() => {
        setToasts((current) => current.filter((item) => item.id !== toast.id));
      }, TOAST_DURATION),
    );

    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [toasts]);

  const renderedToasts = useMemo(() => toasts.slice(-3), [toasts]);

  return (
    <div className={classes.host} aria-live="polite" aria-atomic="true">
      {renderedToasts.map((toast) => (
        <div key={toast.id} className={`${classes.toast} ${toastClassName(toast.type)}`}>
          <span className={classes.icon} aria-hidden="true">
            {toastIcon(toast.type)}
          </span>
          <div className={classes.body}>
            <span className={classes.title}>
              {toast.type ? toast.type.charAt(0).toUpperCase() + toast.type.slice(1) : "Info"}
            </span>
            <div className={classes.message}>{toast.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
